import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { useAuthStore } from "@/features/auth/hooks";
import { useCompanyStore } from "@/features/company/hooks";
import CompanySnapshotCard from "../components/CompanySnapshotCard";
import ESGOverviewCard from "../components/ESGOverviewCard";
import { PageHeader } from "@/components/common";
import { LayoutDashboard } from "lucide-react";
import { Can } from "@/hooks/useCan/Can";
import NXMapCard from "../components/NXMapCard";
import NXPlanCard from "../components/NXPlanCard";
import { useOnboardingStore } from "@/features/onboarding";
import { motion } from "framer-motion";
import { checkUserFromRole } from "@/helpers";
import { useOrganizationStore } from "@/features/organization";
import OrganizationsInfo from "../components/OrganizationsInfo";
const DashboardPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { company, companyLoading, fetchCompany } = useCompanyStore();
  const { onboardData, fetchOnboard } = useOnboardingStore();
  const { user, userRole } = useAuthStore();
  const userCompanyId = user?.companyId || null;
  const hasCustomRoleName =
    typeof user?.customRoleName === "string" && user.customRoleName.trim();
  const normalizedUserRole = (userRole || user?.role || "")
    .toString()
    .trim()
    .toLowerCase();
  const canVisitProjects =
    !hasCustomRoleName &&
    (normalizedUserRole === "super_admin" ||
      (normalizedUserRole === "admin" &&
        ["69f812e01430c8fc738ce5b0"].includes(
          (userCompanyId || "").toString(),
        )));
  const companyId =
    company?.companyId || company?._id || company?.id || userCompanyId;

  // "Visit NXPlan" is gated per-org via the company flag (TG only for now),
  // independent of role/dev-host
  const canVisitNxplan = Boolean(company?.nxplanAccessEnabled);

  const permissions = user?.permissions || [];
  const userOrgIds = user?.organizationUnitId || [];
  const userOrgIdsArray = userOrgIds.map((u) => u.id); // ['6965e964474a8f99a97d6d82', ...]
  const { tree, loading, fetchHierarchy } = useOrganizationStore();
  // Fetch tree on mount only — retrying on empty causes an infinite loop
  // when the API fails (store resets tree to [] on error, re-triggering the effect).
  useEffect(() => {
    fetchHierarchy().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return only directly-assigned nodes (with their assigned children).
  // Unassigned intermediate nodes are skipped, but their names are threaded
  // into _breadcrumbPath so each node always carries its true ancestor chain.
  const buildUserOrgSubtree = (nodes, assignedIds, ancestorPath = []) => {
    const result = [];
    for (const node of nodes) {
      const currentPath = [...ancestorPath, node.name];
      if (assignedIds.includes(node.id)) {
        result.push({
          ...node,
          _breadcrumbPath: ancestorPath,
          children: buildUserOrgSubtree(
            node.children || [],
            assignedIds,
            currentPath,
          ),
        });
      } else {
        // Not assigned — skip this node but accumulate its name for descendants
        result.push(
          ...buildUserOrgSubtree(node.children || [], assignedIds, currentPath),
        );
      }
    }
    return result;
  };

  // Build the list of orgs to display:
  // - If assigned to parent company, show the parent company node with
  //   only the additionally-assigned child orgs nested under it.
  // - Otherwise show only the directly-assigned orgs (and their assigned children).
  const parentName = tree.length > 0 ? tree[0].name : null;
  const assignedChildren = buildUserOrgSubtree(
    tree.length > 0 ? tree[0].children || [] : [],
    userOrgIdsArray,
    parentName ? [parentName] : [],
  );
  const userOrgTree =
    user?.assignedToParentCompany && tree.length > 0
      ? [{ ...tree[0], _breadcrumbPath: [], children: assignedChildren }]
      : assignedChildren;

  const isUserLevel = checkUserFromRole(userRole);

  const handleRefresh = () => {
    if (companyId) {
      fetchCompany({ companyId, force: true });
    }
  };

  useEffect(() => {
    if (!userCompanyId) {
      return;
    }
    fetchCompany({ companyId: userCompanyId });
  }, [userCompanyId]);

  useEffect(() => {
    fetchOnboard();
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title={t("companyInformation.title")}
        description={t("companyInformation.description")}
        icon={LayoutDashboard}
      />
      <Can
        resource="company_information"
        action="view"
        defaultView={isUserLevel}
        userPermissions={permissions}
        showMessage={true}
      >
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
          {/* {isUserLevel ? (
            <OrganizationsInfo organizations={userOrgTree} loading={loading} />
          ) : ( */}
          <CompanySnapshotCard
            onEdit={() => navigate(PATHS.COMPANY_SETUP)}
            isUserLevel={isUserLevel}
          />
          {/* )} */}

          <div className="grid grid-cols-1 gap-4 lg:col-span-2">
            <ESGOverviewCard
              company={company}
              loading={companyLoading}
              onRefresh={handleRefresh}
            />
            {onboardData && onboardData?.nxmap && (
              <motion.div
              // variants={itemVariants}
              >
                <NXMapCard
                  canVisitProjects={canVisitProjects}
                  permissions={permissions}
                  isUserLevel={isUserLevel}
                  baseYear={onboardData?.onboard?.baseYear}
                  organizationBoundary={
                    onboardData?.onboard?.organizationBoundary
                  }
                  reportingYear={onboardData?.onboard?.reportingYear}
                  reportingStandard={onboardData?.onboard?.reportingStandard}
                  data={onboardData?.nxmap}
                  onOpen={() =>
                    navigate(`${PATHS.ONBOARD}/nxmap`, {
                      state: { from: "dashboard" },
                    })
                  }
                />
              </motion.div>
            )}
            {onboardData && onboardData?.nxplan && (
              <motion.div
              // variants={itemVariants}
              >
                <NXPlanCard
                  canVisitNxplan={canVisitNxplan}
                  baseYear={onboardData?.onboard?.baseYear}
                  reportingYear={onboardData?.onboard?.reportingYear}
                  organizationBoundary={
                    onboardData?.onboard?.organizationBoundary
                  }
                  permissions={permissions}
                  isUserLevel={isUserLevel}
                  data={onboardData?.nxplan}
                  onOpen={() =>
                    navigate(`${PATHS.ONBOARD}/nxplan`, {
                      state: { from: "dashboard" },
                    })
                  }
                />
              </motion.div>
            )}
          </div>
        </div>
      </Can>
    </div>
  );
});

export default DashboardPage;
